/**
 * Which keyboard CONTEXTS the app is currently offering.
 *
 * `lib/keyboard.ts` has always carried `requires?: 'list' | 'record'` on every
 * registry entry, documented as "Context the action needs; the sheet greys out
 * what does not apply" — and nothing read it. The `?` sheet filtered on `group`
 * alone, so on `/tasks` all seven ON A RECORD rows printed at full strength
 * (`--text-secondary`, no `aria-disabled`) while every one of them was inert:
 * `a`, `s`, `e`, `n`, `r` and `l` opened no menu, no dialog, moved no focus and
 * changed no route. The sheet is the product's global reference for the
 * keyboard, so it was the wrong document that was lying.
 *
 * A surface declares what it is while it is mounted; the sheet reads the
 * declaration. Ref-counted, because the Inbox mounts a list AND a record pane at
 * the same time and both claims have to survive the other unmounting.
 *
 * This is deliberately NOT derived from the route table or from the DOM. A
 * route table would have to be kept in step with the screens by hand — the
 * class of drift this whole file exists to end — and a DOM sniff would be
 * guessing at which `[role=grid]` counts.
 */

import { useEffect, useSyncExternalStore } from 'react';

export type ShortcutContextKind = 'list' | 'record';

const counts: Record<ShortcutContextKind, number> = { list: 0, record: 0 };
const listeners = new Set<() => void>();

/** A string rather than an object, so `useSyncExternalStore` gets a stable snapshot. */
let snapshot = '';

function recompute(): void {
  const next = `${counts.list > 0 ? 'l' : ''}${counts.record > 0 ? 'r' : ''}`;
  if (next === snapshot) return;
  snapshot = next;
  for (const listener of listeners) listener();
}

/** Imperative form. Returns the release. */
export function claimShortcutContext(kind: ShortcutContextKind): () => void {
  counts[kind] += 1;
  recompute();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    counts[kind] = Math.max(0, counts[kind] - 1);
    recompute();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): string {
  return snapshot;
}

function getServerSnapshot(): string {
  return '';
}

export interface ShortcutContexts {
  list: boolean;
  record: boolean;
}

/** What the `?` sheet reads to decide which rows apply where the reader is. */
export function useShortcutContexts(): ShortcutContexts {
  const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { list: value.includes('l'), record: value.includes('r') };
}

/**
 * "While this component is mounted (and `active`), this screen offers `kind`."
 *
 * A list surface claims `list`; a record surface claims `record` only once the
 * record it binds its keys against has actually loaded, so the two agree by
 * construction — `useShortcut('assign', …, { enabled: Boolean(inquiry) })` and
 * this call take the same condition.
 */
export function useProvideShortcutContext(kind: ShortcutContextKind, active = true): void {
  useEffect(() => {
    if (!active) return;
    return claimShortcutContext(kind);
  }, [kind, active]);
}
